/**
 * VIP / 群 VIP / 官方客服徽标的纯逻辑（21g）。渲染组件 shell/VipBadge.tsx。
 *
 * 档位与文案的**权威来源是后端**（PROMPT「协议以后端为准」）：
 * - 个人：`models.UserVipLevels` = 1 白银会员 / 2 黄金会员 / 3 铂金会员；
 *   `UserVipLevelName` 对未定义等级回退 `VIP{n}`（users.go:134/141）。
 * - 群：`models.VipLevels` = 1 200人群 / 2 1000人群 / 3 2000人群（group.go:170）。
 *   **后端没有 4 档**，旧版客户端 `vip_badge.dart` 多画了一档 V4——以后端为准不做。
 * - `999 = 官方客服`：后端没有这个常量，是旧版客户端的约定
 *   （`vip_badge.dart` `level == 999 && !isGroup`）。保留，因为它是产品语义不是协议。
 *
 * 过期处理在后端：`EffectiveVipLevel()` 已过期下发 0，前端不判过期。
 *
 * 视觉用**旧版原始素材**（21g-5，人工指定）：`scripts/gen-vip-assets.mjs` 把
 * `im_flutterv3/assets/images/` 里的大图按旧版 `vip_badge.dart` 的裁切矩形烘焙成
 * `public/icons/vip/*.webp`。`icon` 字段给出产物路径；**后端定义之外的更高档位
 * 没有对应素材**（`icon: null`），组件回落到纯 CSS 徽标——与旧版 `bounds==null`
 * 时画文字胶囊的兜底同口径。
 */

/** 徽标视图数据；null = 不是 VIP，不渲染 */
export interface VipBadgeView {
  /** 无素材时回落的短文字（列表行空间有限，只显示它） */
  readonly short: string;
  /** 完整文案（title/alt/aria-label，无障碍与 hover 用） */
  readonly label: string;
  /** 配色档（仅 icon=null 的回落路径用）：official / 个人三档 / vn / group */
  readonly tone: 'official' | 'v1' | 'v2' | 'v3' | 'vn' | 'group';
  /** 徽标图路径；null = 该档位没有素材，组件画 CSS 回落徽标 */
  readonly icon: string | null;
}

/** 徽标素材目录（gen-vip-assets.mjs 的产物） */
const ICON_BASE = '/icons/vip';

const USER_VIP_NAMES: Readonly<Record<number, string>> = {
  1: '白银会员',
  2: '黄金会员',
  3: '铂金会员',
};

const GROUP_VIP_NAMES: Readonly<Record<number, string>> = {
  1: '200人群',
  2: '1000人群',
  3: '2000人群',
};

/** 官方客服的个人等级（旧版客户端约定，后端无此常量） */
export const OFFICIAL_SUPPORT_LEVEL = 999;

/** 个人 VIP 徽标。level ≤ 0 → null（后端已把过期折成 0） */
export function userVipBadge(level: number): VipBadgeView | null {
  if (!Number.isFinite(level) || level <= 0) return null;
  if (level === OFFICIAL_SUPPORT_LEVEL) {
    return {
      short: '官方',
      label: '官方客服',
      tone: 'official',
      icon: `${ICON_BASE}/official.webp`,
    };
  }
  const name = USER_VIP_NAMES[level];
  if (name !== undefined) {
    return {
      short: `V${level}`,
      label: `VIP ${level} · ${name}`,
      tone: `v${level}` as 'v1' | 'v2' | 'v3',
      icon: `${ICON_BASE}/user-v${level}.webp`,
    };
  }
  // 后端 UserVipLevelName 的回退分支。旧版额外画了一档 V4 素材，这里沿用；
  // 再高的档位后端没有定义、也没有素材 → icon=null 走 CSS 回落
  if (level === 4) {
    return { short: 'V4', label: 'VIP4', tone: 'vn', icon: `${ICON_BASE}/user-v4.webp` };
  }
  return { short: `V${level}`, label: `VIP${level}`, tone: 'vn', icon: null };
}

/** 群 VIP 徽标。level ≤ 0 → null */
export function groupVipBadge(level: number): VipBadgeView | null {
  if (!Number.isFinite(level) || level <= 0) return null;
  const name = GROUP_VIP_NAMES[level];
  // 素材只有 V1~V4（旧版 group_vip_levels 三联图 + group_v4_badge）；
  // 后端 VipLevels 只定义到 3，V4 沿用旧版素材，更高档位走 CSS 回落
  const icon = level >= 1 && level <= 4 ? `${ICON_BASE}/group-v${level}.webp` : null;
  return {
    short: `V${level}`,
    label: name !== undefined ? `VIP 群 V${level} · ${name}` : `VIP 群 V${level}`,
    tone: 'group',
    icon,
  };
}

/**
 * 会话行用：私聊看 vipLevel、群聊看 groupLevel（旧版 chat_list_item.dart:103/105
 * 同口径——两者互斥，不会同时出现）。
 */
export function conversationVipBadge(
  isGroup: boolean,
  vipLevel: number,
  groupLevel: number,
): VipBadgeView | null {
  return isGroup ? groupVipBadge(groupLevel) : userVipBadge(vipLevel);
}

/** 群内身份徽标（21g-2，旧版 `sender_role_badge.dart`）；null = 普通成员不渲染 */
export interface RoleBadgeView {
  readonly label: string;
  readonly tone: 'owner' | 'admin';
}

/**
 * 发送者群内身份：1=管理员 2=群主，其余不显示（旧版同口径）。
 * 仅群聊有意义——私聊的 senderRole 后端恒 0，调用方无需额外判断。
 */
export function senderRoleBadge(role: number): RoleBadgeView | null {
  if (role === 2) return { label: '群主', tone: 'owner' };
  if (role === 1) return { label: '管理员', tone: 'admin' };
  return null;
}

/**
 * 靓号（premium_no）徽标（21g-4，旧版 `premium_badge.dart`）。
 *
 * 旧版是一张纯图片（无文字，semanticLabel='靓号'）——21g-5 起新版**用的就是同一张
 * 素材**（`premium.webp`，gen-vip-assets.mjs 产出），号码本身放 title。
 *
 * 归一化照搬旧版 `normalizedPremiumNo`：去空白、**去掉前导 `@`**、
 * 空串或字面量 `"null"`（大小写不敏感）都算没有靓号——后端某些接口把
 * 空值序列化成了字符串 "null"，这是旧版踩出来的坑，照搬。
 */
export function normalizePremiumNo(value: string | null | undefined): string {
  const trimmed = (value ?? '').trim().replace(/^@+/, '');
  if (trimmed === '' || trimmed.toLowerCase() === 'null') return '';
  return trimmed;
}

export interface PremiumBadgeView {
  /** 归一化后的号码（title 用） */
  readonly premiumNo: string;
  readonly label: string;
  /** 靓号徽标图（旧版 premium_badge.png 同一张） */
  readonly icon: string;
}

/** 靓号徽标；null = 没有靓号不渲染 */
export function premiumNoBadge(value: string | null | undefined): PremiumBadgeView | null {
  const no = normalizePremiumNo(value);
  return no === ''
    ? null
    : { premiumNo: no, label: `靓号 ${no}`, icon: `${ICON_BASE}/premium.webp` };
}
